import React, { useMemo, Fragment, useEffect } from "react";
import {
    MaterialReactTable,
    MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
    MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
    MRT_ToggleFiltersButton as MRTToggleFiltersButton
} from 'material-react-table';
import Typography from "@mui/material/Typography";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { useViewClientAccountListMutation } from "../../../../reactQueries/AppApi/rqHooksForClientAccount";
import { dateformatConverter } from "../../../../common/utilities/dates/dateformatConverter";
import {
  useGetAccountAdminListQuery,
  useTrackActivityMutation,
} from "../../../../reactQueries/commonRQHooks";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import {
  clientAccountCreatePath,
  clientAccountEditPath,
} from "../../../../routes/routePaths";
import NoTableDataComp from "../../../../common/otherCommonComponents/NoTableDataComp";
import MpgErrorDialog from "../../../../common/dialog/MpgErrorDialog";
import HtmlTooltip from "../../../../common/tooltips/HtmlTooltip";
import { useGetLoggedInUserDetails } from "../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getAccessTypes } from "../../../../hooksAndFunctions/functions/getAccessTypes";
import ExportButton from "../../../../common/otherCommonComponents/ExportButton";

const TooltipCell = ({ renderedCellValue }) => (
  <HtmlTooltip title={renderedCellValue}>{renderedCellValue}</HtmlTooltip>
);

const AuditDateCell = ({ renderedCellValue }) =>
  renderedCellValue
    ? dateformatConverter(renderedCellValue, "DD MMM YYYY hh:mm A")
    : "";

const returnLabel = (key, labelArray = []) => {
  return labelArray.find((label) => label.Key === key)?.Phrase || "";
};

const getClientAccountName = (row) => row?.ClientAccountName || "";

const getClientAccountCodes = (row) =>
  (row?.ClientAccountCodes || []).map((item) => item.clientCode).toString();

const createAdminEmailsAccessor = (accountAdmins) => (row) => {
  const emailList = [];
  (row?.ClientAccountAdmins || []).forEach(({ adminId }) => {
    const foundAdmin = accountAdmins?.find(
      (admin) => admin.adminId === adminId
    );
    if (foundAdmin) emailList.push(foundAdmin.emailAddress);
  });
  return emailList.toString() || "";
};

const getAccountStatus = (row) => (row.IsActive ? "Active" : "Inactive");

const getCreatedBy = (row) => row?.CreatedBy || "";

const getCreatedDate = (row) =>
  row?.CreatedTimestamp ? new Date(row.CreatedTimestamp) : null;

const getUpdatedBy = (row) => row?.UpdatedBy || "";

const getUpdatedDate = (row) => {
  if (!row?.UpdatedBy) return null;
  return row?.UpdatedTimestamp ? new Date(row.UpdatedTimestamp) : null;
};

const ViewClientAccount = () => {
  const { refClientAccountCmsData } = useOutletContext();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const {
    clientPortalAdminRoleId,
    isEnterpriseAdmin,
    id: userId,
  } = loggedInUser;

  const { mutate: trackActivityMutate } = useTrackActivityMutation();
  const { isEnterpriseAdmin: isEnterpriseAdminType, clientPortalAdminType } = getAccessTypes();
  const {
    hasA1Access,
    hasA2Access,
    hasA4Access,
  } = clientPortalAdminType;

  const canExportXls = isEnterpriseAdminType || hasA1Access;

  const {
    mutate: getClientAccountList,
    isSuccess,
    data: clientAccountData,
    isError,
    isLoading: isLoadingOnAccList,
  } = useViewClientAccountListMutation();

  useEffect(() => {
    getClientAccountList({
      loggedInUserId: userId,
      roleId: clientPortalAdminRoleId,
      isEnterpriseAdmin,
    });
  }, [loggedInUser]);

  const { data: accountAdminListFromAPI } = useGetAccountAdminListQuery();

  const fetchClientAccountCmsData = (key) => {
    const labels = refClientAccountCmsData?.Components?.ClientAccountsList?.Labels;
    return returnLabel(key, labels);
  };

  const fetchCommonComponentCMSLabel = (key) => {
    const labels = refClientAccountCmsData?.Components?.CommonComponent?.Labels;
    return returnLabel(key, labels);
  };

  const clientAccountsTitle = fetchClientAccountCmsData('ClientAccountsList.ClientAccountsTitle');
  const exportFileName = `${clientAccountsTitle}.xlsx`;
  const exportWorksheetName = clientAccountsTitle;

  const exportColumns = [
    { id: 'clientAccountName', header: fetchClientAccountCmsData("ClientAccountsList.ClientAccountTableHeader"), getValue: (row) => row?.ClientAccountName || '' },
    { id: 'clientAccountCodes', header: fetchClientAccountCmsData("ClientAccountsList.ClientCodes"), getValue: (row) => (row?.ClientAccountCodes || []).map((item) => item.clientCode).filter(Boolean).join(', ') },
    {
      id: 'clientAccountAdmins',
      header: fetchClientAccountCmsData("ClientAccountsList.Admins"),
      getValue: (row) => (row?.ClientAccountAdmins || [])
        .map((item) => accountAdminListFromAPI?.find((admin) => admin.adminId === item.adminId)?.emailAddress || '')
        .filter(Boolean)
        .join(', ')
    },
    { id: 'isActive', header: fetchClientAccountCmsData("ClientAccountsList.Status"), getValue: (row) => row?.IsActive ? 'Active' : 'Inactive' },
    { id: 'created_by', header: fetchClientAccountCmsData("ClientAccountsList.CreatedBy") || "Created By", getValue: (row) => row?.CreatedBy || '' },
    { id: 'created_date', header: fetchClientAccountCmsData("ClientAccountsList.CreatedDate") || "Created Date", getValue: (row) => row?.CreatedTimestamp ? dateformatConverter(row.CreatedTimestamp, "DD MMM YYYY hh:mm A") : '' },
    { id: 'edited_by', header: fetchClientAccountCmsData("ClientAccountsList.EditedBy") || "Edited By", getValue: (row) => {
        const updatedBy = row?.UpdatedBy;
        return updatedBy || '';
      }
    },
    { id: 'edited_on', header: fetchClientAccountCmsData("ClientAccountsList.EditedOn") || "Edited On", getValue: (row) => {
        const updatedBy = row?.UpdatedBy;
        if (!updatedBy) return '';
        return dateformatConverter(row?.UpdatedTimestamp, "DD MMM YYYY hh:mm A");
      }
    },
    { id: 'publishedOn', header: fetchClientAccountCmsData("ClientAccountsList.PublishedOn") || 'Published On', getValue: (row) => row?.CreatedTimestamp ? dateformatConverter(row.CreatedTimestamp, "DD MMM YYYY hh:mm A") : '' },
  ];

  const columns = useMemo(
    () => [
      {
        id: "clientAccountName",
        header: fetchClientAccountCmsData(
          "ClientAccountsList.ClientAccountTableHeader"
        ),
        accessorFn: getClientAccountName,
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccountCmsData(
            "ClientAccountsList.PlaceholderClientAccounts"
          ),
        },
      },
      {
        id: "clientAccountCodes",
        header: fetchClientAccountCmsData("ClientAccountsList.ClientCodes"),
        accessorFn: getClientAccountCodes,
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccountCmsData(
            "ClientAccountsList.PlaceholderClientCodes"
          ),
        },
      },
      {
        id: "clientAccountAdmins",
        header: fetchClientAccountCmsData("ClientAccountsList.Admins"),
        accessorFn: createAdminEmailsAccessor(accountAdminListFromAPI),
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccountCmsData(
            "ClientAccountsList.PlaceholderAdmins"
          ),
        },
      },
      {
        accessorFn: getAccountStatus,
        id: "isActive",
        header: fetchClientAccountCmsData("ClientAccountsList.Status"),
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccountCmsData(
            "ClientAccountsList.PlaceholderStatus"
          ),
        },
      },
      {
        id: "created_by",
        header: fetchClientAccountCmsData("ClientAccountsList.CreatedBy") || "Created By",
        accessorFn: getCreatedBy,
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccountCmsData(
            "ClientAccountsList.PlaceholderCreatedBy"
          ) || "Filter by Created By",
        },
      },
      // ✅ Renamed Published On → Created Date
      {
        accessorFn: getCreatedDate,
        id: "created_date",
        header: fetchClientAccountCmsData("ClientAccountsList.CreatedDate") || "Created Date",
        Cell: AuditDateCell,
        filterFn: "contains",
        sortingFn: "datetime",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccountCmsData(
            "ClientAccountsList.PlaceholderCreatedDate"
          ) || "Filter by Created Date",
        },
      },
      //  Edited By — only shows a value if the record was edited
      // (UpdatedBy exists AND is different from CreatedBy)
      {
        id: "edited_by",
        header: fetchClientAccountCmsData("ClientAccountsList.EditedBy") || "Edited By",
        accessorFn: getUpdatedBy,
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccountCmsData(
            "ClientAccountsList.PlaceholderEditedBy"
          ) || "Filter by Edited By",
        },
      },
      // ✅ Edited On — only shows a value if the record was edited
      // (UpdatedBy exists AND is different from CreatedBy)
      {
        id: "edited_on",
        header: fetchClientAccountCmsData("ClientAccountsList.EditedOn") || "Edited On",
        accessorFn: getUpdatedDate,
        Cell: AuditDateCell,
        filterFn: "contains",
        sortingFn: "datetime",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccountCmsData(
            "ClientAccountsList.PlaceholderEditedOn"
          ) || "Filter by Edited On",
        },
      },
    ],
    [accountAdminListFromAPI, refClientAccountCmsData]
  );

  let navigate = useNavigate();

  return (
    <Fragment>
      {!!isLoadingOnAccList &&
        fetchCommonComponentCMSLabel("Common.Waitloading")}

      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {fetchClientAccountCmsData("ClientAccountsList.ClientAccountsTitle")}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        m={1}
      >
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to={clientAccountCreatePath}
          disabled={hasA2Access || hasA4Access}
        >
          {fetchClientAccountCmsData("ClientAccountsList.SetupClientAccount")}
        </Button>
      </Stack>

      {!!isSuccess && !clientAccountData?.data?.length && <NoTableDataComp />}

      {!!isSuccess && !!clientAccountData?.data?.length && (
        <MaterialReactTable
          columns={columns}
          data={clientAccountData?.data || []}
          layoutMode="grid"
          enableGlobalFilter={false}
          enableDensityToggle={false}
          initialState={{
            density: "compact",
            showColumnFilters: true,
          }}
          state={{
            isLoading: isLoadingOnAccList,
            columnPinning: {
              left: ["mrt-row-actions", "clientAccountName"],
            },
          }}
          enableStickyHeader
          enableRowActions
          enablePinning
          renderToolbarInternalActions={({ table }) => (
            <>
              {canExportXls && (
                <ExportButton
                  table={table}
                  tooltipTitle={fetchClientAccountCmsData("ClientAccountsList.ExportToXLS")}
                  trackActivity={trackActivityMutate}
                  userId={userId}
                  exportColumns={exportColumns}
                  fileName={exportFileName}
                  worksheetName={exportWorksheetName}
                />
              )}
              <MRTToggleFiltersButton table={table} />
              <MRTShowHideColumnsButton table={table} />
              <MRTFullScreenToggleButton table={table} />
            </>
          )}
          muiTablePaginationProps={{
            labelRowsPerPage:
              fetchCommonComponentCMSLabel("Common.Rowsperpage"),
          }}
          displayColumnDefOptions={{
            "mrt-row-actions": {
              header: fetchClientAccountCmsData("ClientAccountsList.Actions"),
            },
          }}
          renderRowActions={({ row, table }) => {
            const Id = row.original.Id;
            return (
              <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
                <IconButton
                  onClick={() => {
                    navigate(clientAccountEditPath({ id: Id }));
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            );
          }}
        />
      )}

      <MpgErrorDialog open={!!isError} />
    </Fragment>
  );
};

export default ViewClientAccount;
